import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { useArtwork } from '~/api/artwork'
import { ApiError, api } from '~/api/client'
import { act } from '~/beacon'
import type { EventDetailPublic, TicketLink } from '~/api/types'

import { Decide } from './Decide'
import type { Seed } from './seed'

import s from './EventSheet.module.css'

/**
 * The event sheet, ported from `_calendar_page.py`'s `openEvent`, `standIn`, `ticketLink`,
 * `money` and the `<dialog id="sheet">` it all writes into.
 *
 * This file is the dialog and the top of what it holds: the close, the artwork, the title,
 * the night, the room, the ticket links and the promoter's own words. The bill, who else is
 * going and the editor's panel are composed in through the two slots below — they are
 * separate files, and the sheet does not know what is in them.
 *
 * WHAT CHANGED FROM THE PYTHON, and why
 *
 * `openEvent` drew the title and the night from `DATA.entries`, the calendar's snapshot, and
 * only fetched `/event/<id>` for the rest. It fell back to `standIn` — a second fetch of the
 * same detail — for a row the snapshot does not carry. That was two paths, and the merged
 * row, the deleted row and the shared link, which were on the second, are the case that was
 * broken twice.
 *
 * So there is one path here. Every opening fetches `/event/<id>` once, through the one effect
 * below, and whatever it answers — the gig, the gig it was folded into, gone, failed — is what
 * the sheet shows from then on. A clicked row may hand over a `seed` (`./seed`), and that is
 * paint and nothing more: the row's title, picture and night line drawn in the gap before the
 * answer, in place of `loading…`. It never skips, delays or changes the fetch, it is never
 * drawn again once this id has had an answer, and an opening with no seed — a pasted link, a
 * reload, a hop from inside the sheet — is the standin it always was.
 *
 * The address bar is NOT touched here. `openEvent` did `history.replaceState` to `/e/<id>`
 * on open and back to `/calendar` on close; under react-router that would desync the router
 * from the URL. It belongs to the calendar that owns this sheet's mounting.
 */

/** The signed-in viewer. Local by request; `Viewer` in `~/api/types` is the same shape. */
export interface Me {
  id: number
  name: string
  username: string
  isEditor: boolean
}

/** What a slot is handed. The detail is fetched once, here, so nothing composed into the
 *  sheet has to fetch it again — and `me` rides along because all three slots need it. */
export interface SheetContext {
  detail: EventDetailPublic
  me: Me | null
  /**
   * Fetch this event again.
   *
   * The editor's controls change what the sheet is showing — a name added to the line-up, a
   * name taken off — and the sheet is the only thing holding that answer. Without a way to
   * ask again, a correction succeeded on the server and left the panel showing what it had
   * before, which is indistinguishable from the control not working.
   */
  reload: () => void
  /**
   * Change what the sheet is showing, without asking the server again.
   *
   * `reload` is the right answer when a control changes something the browser cannot
   * compute — a name added to the line-up comes back with an id and a photograph. It is the
   * wrong answer for a mark: following an act flips one glyph, and re-fetching the event to
   * learn that flashes the whole sheet, artwork and all, on every press.
   */
  patch: (change: (was: EventDetailPublic) => EventDetailPublic) => void
}

/** A slot: a node, or a function of the loaded detail for the parts that need it. */
export type Slot = ReactNode | ((sheet: SheetContext) => ReactNode)

function fill(slot: Slot | undefined, sheet: SheetContext): ReactNode {
  return typeof slot === 'function' ? slot(sheet) : (slot ?? null)
}

/**
 * A price, in pounds, from pence.
 *
 * Ported from `money`, which took a whole entry; this takes the two numbers, because the one
 * caller left in this file is the ticket link and a ticket link's price is its own. The
 * `.00` is dropped on a round number and kept on anything else, and `priceMax` is only
 * printed when it differs — a range of £15–£15 is a price written twice.
 */
export function money(priceMin: number | null, priceMax: number | null): string {
  if (priceMin == null) return ''
  const f = (n: number) => '£' + (n % 1 ? n.toFixed(2) : String(n))
  return priceMax && priceMax !== priceMin
    ? `${f(priceMin / 100)}–${f(priceMax / 100)}`
    : f(priceMin / 100)
}

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * The ticketing platforms, by the host they answer on.
 *
 * Only platforms. A venue selling its own tickets — barbican.org.uk, theo2.co.uk — is not
 * in here, because the stored vendor already names the venue and "Barbican" is a better
 * button than anything a domain would give. Nor is songkick.com, for the opposite reason
 * given at `seller` below.
 *
 * Matched on the host and on each shorter suffix of it, so `theatre.ticketmaster.co.uk`
 * finds `ticketmaster.co.uk` without needing a row of its own.
 */
const PLATFORM: Record<string, string> = {
  'dice.fm': 'DICE',
  'ra.co': 'RA',
  'skiddle.com': 'Skiddle',
  'ticketmaster.co.uk': 'Ticketmaster',
  'ticketmaster.com': 'Ticketmaster',
  'universe.com': 'Universe',
  'ticketweb.uk': 'TicketWeb',
  'ticketweb.co.uk': 'TicketWeb',
  'seetickets.com': 'See Tickets',
  'gigantic.com': 'Gigantic',
  'eventim.co.uk': 'Eventim',
  'axs.co.uk': 'AXS',
  'axs.com': 'AXS',
  'wegottickets.com': 'WeGotTickets',
}

/** The platform a link actually opens, or null when it is not one this knows. */
export function platformOf(url: string): string | null {
  let host: string
  try {
    host = new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return null
  }
  const parts = host.split('.')
  for (let i = 0; i < parts.length - 1; i += 1) {
    const suffix = parts.slice(i).join('.')
    if (suffix in PLATFORM) return PLATFORM[suffix] ?? null
  }
  return null
}

/**
 * Whether a link is Songkick's redirector rather than a shop.
 *
 * Decided by the host, because the host is the one thing about the link Songkick does not
 * choose the wording of. `vendor` on these rows is the name of the seller the hop lands on
 * — "Ticketmaster UK", "See Tickets", or one of Songkick's generic labels — so a test on the
 * vendor string never sees a hop at all, and `offered` below silently kept every one.
 *
 * `songkick.com` itself and any subdomain of it; a host that merely ends in the characters
 * (`notsongkick.com`) is not. A URL that does not parse is not a hop, and not an error: the
 * link is still offered and the button still opens whatever it opens.
 */
export function isHop(url: string): boolean {
  let host: string
  try {
    host = new URL(url).hostname
  } catch {
    return false
  }
  return host === 'songkick.com' || host.endsWith('.songkick.com')
}

/**
 * What to call the seller: the platform the button actually opens.
 *
 * It named the supplier instead, and the two are not the same thing. 918 links labelled
 * TICKETMASTER open universe.com and 194 open ticketweb.uk — both Ticketmaster-owned, which
 * is not a fact a reader has or should need. What they have is a page that says
 * TICKETMASTER and a tab that says Universe.
 *
 * A songkick.com link is the exception, and deliberately so: it is a redirect *to* the named
 * seller rather than a shop, so the button reading "See Tickets" is describing where you
 * land. Naming the hop would be the same mistake in the other direction.
 *
 * `provider` remains the last resort. It is a closed set of eight — it is what the code
 * routes on — and four of the vendors Songkick names collapse to `other` inside it, which
 * put a button reading "OTHER" under Buy tickets on a night whose answer was "Eventim".
 * `vendor` is the column added to fix that; NULL still means nobody said.
 */
export function seller(ticket: TicketLink): string {
  return platformOf(ticket.url) ?? ticket.vendor ?? ticket.provider
}

/**
 * A Songkick link is not a checkout. It goes to a page that lists whoever is selling, so on
 * a night that already has a DICE link it is a second click to reach the first one — noise
 * beside the real answer, and the thing `seller` above was written to prevent: a button
 * reading "Songkick" as though Songkick were selling the ticket.
 *
 * On the nights where it is the only link it is the whole way through. So it is dropped
 * where there is something better and kept where there is not, and "something better" is
 * any link that is not a hop — not a link to the same seller, because a hop's price is
 * Songkick's page-wide aggregate rather than that seller's, so nothing a reader can act on
 * is lost with it.
 *
 * A hop is told by its host (`isHop`), not by its `vendor`. The vendor on a hop names the
 * seller it lands on — the same name the direct link gets from `PLATFORM` or nearly: the
 * direct link reads "Ticketmaster" and the hop "Ticketmaster UK", one from each side of
 * `seller`'s `??`, and `onePerSeller` keys on that name, so the pair survived the fold and
 * drew two buttons for one checkout. Dropping the hop first is what makes them one.
 *
 * Dropped here rather than never stored. The link is true and the label is honest — the row
 * stays in `ticket_link`, and this is the sheet deciding what is worth offering.
 *
 * A night that has already happened offers none of them at all. The links do not expire when
 * the gig does, so the sheet went on drawing a Buy tickets section pointing at a checkout for
 * a night nobody could go to — an offer the page itself was greying out three inches above.
 */
export function offered(tickets: readonly TicketLink[], over: boolean): readonly TicketLink[] {
  if (over) return []
  const elsewhere = tickets.filter(t => !isHop(t.url))
  return onePerSeller(elsewhere.length ? elsewhere : tickets)
}

/**
 * One button per seller and tier, keeping the best link of each.
 *
 * `_write_offers` has always held this rule at the ingest — "one link per seller, and a link
 * we already hold wins" — and `calendar.merge`'s ticket-link union went round it. The union
 * dedupes on the URL, which is what the table's own constraint does, and two DICE links for
 * one night are two different URLs. So folding a duplicate gig gave the survivor a second
 * DICE button, and 3,505 future gigs now draw at least one pair reading the same thing;
 * 1,942 did before the union, so this was already true and the union roughly doubled it.
 *
 * Fixed here rather than in the data because the data is not wrong. A fold is reversible and
 * the folded row keeps its own links, so both rows genuinely hold them — this is the sheet
 * deciding what is worth offering, exactly as the Songkick rule above it does.
 *
 * The tier is part of the identity, so Ticketmaster's premium listing keeps its own button:
 * that pair is the one thing a reader can actually choose between.
 *
 * "Best" is the one a reader would rather have: a link with a price beats one without,
 * because a button that names a price is the point of showing it, and the cheaper of two
 * priced links wins. That is what makes this safe to do after the union rather than instead
 * of it — the union is what brought the cheaper price across in the first place.
 */
export function onePerSeller(tickets: readonly TicketLink[]): readonly TicketLink[] {
  const best = new Map<string, TicketLink>()
  for (const ticket of tickets) {
    const key = `${seller(ticket).toLowerCase()}\u0000${ticket.ticket_type ?? ''}`
    const held = best.get(key)
    if (!held || beats(ticket, held)) best.set(key, ticket)
  }
  return [...best.values()]
}

function beats(one: TicketLink, other: TicketLink): boolean {
  if (one.price_min === null) return false
  if (other.price_min === null) return true
  return one.price_min < other.price_min
}

/**
 * One vendor can sell the same night twice. Ticketmaster lists a premium tier as a separate
 * event — `manifest` and `manifest - Venue Premium Tickets` at the same room on the same
 * night — and folding them into one gig leaves two buttons that would otherwise read
 * identically at different prices.
 *
 * So the tier is named, and the ordinary one is named *too*, but only when there is
 * something to tell it apart from. "Ticketmaster · Standard" beside "Ticketmaster · Premium"
 * is the pair a reader can choose between; "Ticketmaster · Standard" on its own is a word
 * that answers a question nobody asked.
 */
function tierOf(ticket: TicketLink, all: readonly TicketLink[]): string {
  if (ticket.ticket_type) return sentenceCase(ticket.ticket_type)
  return all.filter(other => other.provider === ticket.provider).length > 1 ? 'Standard' : ''
}

function Buy({ ticket, all }: { ticket: TicketLink; all: readonly TicketLink[] }) {
  // Each vendor's own price, never the event's. This printed the cheapest price across every
  // link beside *all* of them, so Ticketmaster — which publishes no UK prices at all — wore
  // DICE's £33.75 while selling the same night for more.
  const own = money(ticket.price_min, ticket.price_max)
  const tier = tierOf(ticket, all)

  // A plain anchor, not `AppLink`: this is the one link on the sheet that leaves the site
  // altogether, and `AppLink` is the router's question — ported address or Python's.
  return (
    <a className={s.buy} href={ticket.url} target="_blank" rel="noreferrer">
      {seller(ticket)}
      {tier ? <b>{tier}</b> : null}
      {own ? ` · ${own}` : ''} ↗
    </a>
  )
}

/** Why there is no event to draw. Neither is a spinner, and that is the point. */
type Missing = 'gone' | 'failed'

export interface EventSheetProps {
  id: string
  /**
   * What the clicked row already knew, drawn until `/event` answers for this id. Ignored when
   * its id is not `id`. See `./seed` for what it may hold and why that is all.
   */
  seed?: Seed | null
  me: Me | null
  onClose(): void
  /** Narrow the calendar to this room. A filter, not a navigation — see `pickVenue`. */
  onVenue(slug: string): void
  /**
   * Open the act the title names — used only when `detail.title_act` is set, which is when
   * the line-up has been dropped for saying nothing the title does not.
   *
   * A prop like `onVenue` beside it rather than a link, because the artist opens inside the
   * sheet's own navigation and this component does not know how that is done. Optional: a
   * caller that does not pass it gets a plain heading, which is what the sheet has always
   * had, rather than a title that looks pressable and is not.
   */
  onArtist?(artistId: number): void
  /**
   * The controls row and who else is going, drawn under the night. Another file's; the sheet
   * only knows where they go.
   */
  actions?: Slot
  /** The bill, the other dates and the editor's panel, drawn under the blurb. */
  children?: Slot
}

export function EventSheet({
  id,
  seed = null,
  me,
  onClose,
  onVenue,
  onArtist,
  actions,
  children,
}: EventSheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const [detail, setDetail] = useState<EventDetailPublic | null>(null)
  const [missing, setMissing] = useState<Missing | null>(null)
  /*
   * The id the held `detail` or `missing` is the answer for.
   *
   * Both are set by an effect, and an effect runs after the render that changed the id — so
   * for that one render they still describe the last gig. Drawing them only when they belong
   * to this id is what keeps a hop from A to B from painting A under B's address. It is also
   * what retires the seed: once this id has had an answer, a `reload` that clears `detail`
   * shows the standin rather than the row again, because the row may be older than the answer
   * it would be covering.
   */
  const [answered, setAnswered] = useState<string | null>(null)
  const navigate = useNavigate()

  /*
   * The dialog, opened once and closed once.
   *
   * `showModal` is what makes this modal rather than a `<div>` wearing `aria-modal`: the
   * page behind goes inert, Escape closes, and the browser puts focus inside. The close
   * button is focused explicitly anyway — it is the first focusable descendant, so this
   * agrees with what `showModal` would do, and it does not depend on that being true after
   * a slot adds something above it.
   *
   * `leaving` exists because the cleanup closes the dialog, which fires `close`, which is
   * the handler that tells the parent to unmount this. Without the flag the unmount asks to
   * be unmounted again.
   */
  const leaving = useRef(false)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    const opener = document.activeElement

    const closed = () => {
      if (!leaving.current) onClose()
    }
    dialog.addEventListener('close', closed)

    return () => {
      leaving.current = true
      dialog.removeEventListener('close', closed)
      if (dialog.open) dialog.close()
      // Back to whatever opened the sheet — the chip, the row, the card. Browsers restore
      // this themselves on `close()`, but only when the dialog is still in the document,
      // and React may have taken it out first.
      if (opener instanceof HTMLElement) opener.focus()
    }
    // Once, deliberately, and this is the half that must stay once. `onClose` is not a
    // dependency because a changing handler must not tear down a listener; and `id` must not
    // be one either, because the cleanup latches `leaving` and never lowers it. Re-run on an
    // id change, and from then on `closed` sees `leaving` true and the × tells the parent
    // nothing: the dialog shuts, the address stays on the old gig, and the sheet is stuck.
    // Which is the bug this whole change is about, on a new path.
    //
    // Lowering the latch at the top of the effect does not fix it. Chrome dispatches `close`
    // *asynchronously*, so the `close()` the cleanup just performed lands on the listener the
    // re-run installs — with the latch down that fires `onClose` on the hop itself, closing
    // the sheet the reader has only just opened. Worth knowing that no test here can catch
    // that: `test-setup.ts`'s dialog polyfill dispatches `close` synchronously.

  }, [])

  useEffect(() => {
    // Opening is the part that belongs to the event, so it is its own effect with its own
    // dependency. Nothing here tears anything down, so an id change costs no `close` event
    // and cannot latch anything.
    //
    // It matters when the sheet is mounted with its dialog already shut. `Overlay.shut` closes
    // with `navigate(-1)`, and if that lands somewhere the route still matches — which a
    // duplicate history entry gives you — React keeps this component and the dialog stays
    // down. Nothing reopened it, so every later tap changed the address while the page drew
    // nothing until a reload. The duplicate itself is fixed in `ListView` and `Briefing`; this
    // is the belt to that pair of braces.
    const dialog = dialogRef.current
    if (!dialog || dialog.open) return
    dialog.showModal()
    closeRef.current?.focus()
  }, [id])

  /** Bumped to ask for the event again — see `SheetContext.reload`. */
  const [asked, setAsked] = useState(0)

  useEffect(() => {
    let stale = false
    // Opened from a row, which drew the row's fields at once: timed until the answer is drawn.
    // A reload is not an opening, and neither is a hop that carries no row.
    const opened = !asked && seed?.id === id ? act('event') : undefined
    setDetail(null)
    setMissing(null)
    api
      .event(id)
      .then(loaded => {
        if (stale) return
        opened?.(true)
        setAnswered(id)
        // The Python checked `!detail || !detail.event`, because it read the 404's JSON body
        // rather than its status. `api` throws on a 404, so the check is on the throw — but
        // a 200 with no event is still a sheet with nothing in it.
        if (!loaded || !loaded.event) setMissing('gone')
        else {
          setDetail(loaded)
          // A gig folded into another answers with the one it was folded into, so the id in
          // the bar names a row nothing reads. `replace`, because the reader did not take a
          // step — following an old link should leave them able to go back where they were.
          if (loaded.moved_from) navigate(`/e/${loaded.event.id}`, { replace: true })
        }
      })
      .catch((error: unknown) => {
        if (stale) return
        opened?.(false)
        setAnswered(id)
        // 404 is the ordinary case, not an error: a link to a row that was merged away, or
        // deleted, or that never existed. Anything else is the server failing, and it gets a
        // different sentence — but it must get one. The Python let a non-JSON body throw out
        // of an un-awaited promise, and the sheet sat on "loading…" for ever.
        setMissing(error instanceof ApiError && error.status === 404 ? 'gone' : 'failed')
      })
    return () => {
      stale = true
    }
  }, [id, asked])

  function dismiss() {
    dialogRef.current?.close()
  }

  const current = answered === id
  const answer = current ? detail : null
  // Only before this id's first answer: see `answered`.
  const seeded = !current && seed?.id === id ? seed : null

  return (
    <dialog
      ref={dialogRef}
      className={s.dialog}
      // The backdrop is part of the dialog element, so a click that lands on the dialog
      // itself rather than on anything inside it is a click outside the sheet.
      onClick={event => {
        if (event.target === dialogRef.current) dismiss()
      }}
    >
      <button type="button" ref={closeRef} className={s.close} aria-label="Close" onClick={dismiss}>
        ×
      </button>
      <div className={s.sheet}>
        {/* Drawn here rather than inside `Loaded`, so the seed and the answer draw their
            picture through one `useArtwork`: the row's address until the answer lands, then
            `/event`'s. The swap changes only the `src` of the one `<img>` node, which stays
            mounted, and Chrome keeps painting the picture that node already holds until the
            new one has loaded, so the card's picture stays up while the sheet's arrives. In that
            window the element's `currentSrc` and `naturalWidth` read as though no picture had
            loaded, so neither says what is on screen. The hold depends on the node
            surviving: a `key` on it, or a branch that unmounts it, starts a fresh `<img>`,
            which is empty until its picture loads — as is the hero of a seed with no picture. */}
        <Hero url={answer ? (answer.event.image_url ?? null) : (seeded?.image ?? null)} />
        {answer ? (
          <Loaded
            detail={answer}
            me={me}
            reload={() => setAsked(n => n + 1)}
            /* Guarded against `null`, which cannot happen here — `Loaded` is only rendered
               when `detail` is set — but the state itself is nullable while the fetch is in
               flight, and a cast would be the lie rather than the check. */
            patch={change => setDetail(was => (was ? change(was) : was))}
            onVenue={onVenue}
            {...(onArtist ? { onArtist } : {})}
            actions={actions}
          >
            {children}
          </Loaded>
        ) : seeded ? (
          /* The row's title as plain text, never the act's button: `title_act` is the
             server's to decide, and it arrives with everything else. */
          <div className={s.pad}>
            <h2>{seeded.title}</h2>
            {seeded.night ? <Night {...seeded.night} onVenue={onVenue} /> : null}
          </div>
        ) : (
          <div className={s.pad}>
            <div className={s.spin}>{standin(current ? missing : null)}</div>
          </div>
        )}
      </div>
    </dialog>
  )
}

/**
 * What the sheet says when it has no event.
 *
 * The sheet opens on rows the calendar does not carry — one merged away is off every listing
 * by design, one deleted is hidden, and a link somebody sent you can point past the window
 * the calendar was built over. So "not here" is an ordinary answer and has to read as one.
 * A spinner that never stops says the opposite: that it is coming.
 */
function standin(missing: Missing | null): string {
  if (missing === 'gone') return 'No such event.'
  if (missing === 'failed') return 'That did not load. Try again in a moment.'
  return 'loading…'
}

function Loaded({
  detail,
  me,
  reload,
  patch,
  onVenue,
  onArtist,
  actions,
  children,
}: {
  detail: EventDetailPublic
  me: Me | null
  reload: () => void
  patch: (change: (was: EventDetailPublic) => EventDetailPublic) => void
  onVenue: (slug: string) => void
  onArtist?: (artistId: number) => void
  actions?: Slot
  children?: Slot
}) {
  const event = detail.event
  const sheet: SheetContext = { detail, me, reload, patch }
  const buys = offered(detail.tickets, Boolean(event.past))

  return (
    <div className={s.pad}>
      {/*
        * The title, and for a one-act gig it is also the way through to the act.
        *
        * `title_act` is set only when the line-up would repeat the title exactly, and the
        * server sets it precisely so this can happen: the bill is dropped downstairs and
        * the link it carried moves up here. A heading that is a link is unusual, and it is
        * the honest shape — on this gig the name of the night and the name of the act are
        * the same words, so there is one thing to press and it should be the words.
        */}
      <h2>
        {detail.title_act !== null && onArtist ? (
          <button
            type="button"
            className={s.titlelink}
            onClick={() => onArtist(detail.title_act as number)}
          >
            {event.title}
          </button>
        ) : (
          event.title
        )}
      </h2>
      <Night
        dayRange={event.dayRange ?? null}
        dayLong={event.dayLong ?? ''}
        time={event.time ?? ''}
        venue={event.venue}
        venueSlug={event.venue_slug}
        onVenue={onVenue}
      />

      {/* Save it, and say you are going. Above who else is going, because deciding for
          yourself comes before reading what anybody else decided. */}
      <Decide detail={detail} />

      {fill(actions, sheet)}

      {buys.length ? (
        <div className={s.sec}>
          <h3>Buy tickets</h3>
          <div className={`${s.actions} ${s.buys}`}>
            {buys.map((ticket, at) => (
              <Buy key={`${at}-${ticket.url}`} ticket={ticket} all={buys} />
            ))}
          </div>
        </div>
      ) : null}

      {event.description ? (
        <div className={s.sec}>
          <h3>What it is</h3>
          <p className={s.about}>{event.description}</p>
        </div>
      ) : null}

      {fill(children, sheet)}
    </div>
  )
}

/**
 * The sheet's picture, from whichever address is drawing it — the row's, then `/event`'s.
 *
 * Promoter art is hosted elsewhere and sometimes 404s, or answers 200 with a blank. The
 * Python removed the node outright (`onerror="this.remove()"`); `useArtwork` is the same,
 * keyed by URL so a sheet reopened on a different gig, or handed `/event`'s address after the
 * row's, does not inherit the last one's failure, and with one retry at full size first. It
 * replaced a `brokenSrc` this component threaded down to itself through its own props, which
 * was that state written twice.
 *
 * Both addresses are used exactly as the server sent them. The row's is card-sized and
 * `/event`'s is sheet-sized, and building either here would be a copy of `artwork.served`.
 */
function Hero({ url }: { url: string | null }) {
  const hero = useArtwork(url)
  return hero.src && !hero.broken ? (
    <img className={s.hero} src={hero.src} alt="" decoding="async" onError={hero.onError} />
  ) : null
}

/**
 * The night, the hour and the room — the one rule for that line, drawn from a calendar row's
 * seed and from `/event` alike, so the line cannot change shape when the answer replaces the
 * seed.
 *
 * Every part is pre-formatted in Europe/London by the server, and is read rather than derived
 * for exactly that reason: `starts_at` is UTC, and re-deriving them here would put a second
 * timezone rule in the app. A run says its span rather than its opening night: for a
 * four-night festival "Thursday 27 August" is the wrong fact, and the one the reader is least
 * likely to want.
 *
 * The room is a button only when it has a page; a placeholder room (`TBA`) has no slug.
 */
function Night({
  dayRange,
  dayLong,
  time,
  venue,
  venueSlug,
  onVenue,
}: {
  dayRange: string | null
  dayLong: string
  time: string
  venue: string
  venueSlug: string | null
  onVenue: (slug: string) => void
}) {
  const when = [dayRange || dayLong, time].filter(Boolean)
  return (
    <div className={s.when}>
      {/* Kept from the Python, including the separator that is printed unconditionally: an
          event with neither a day nor an hour opens this line with a bare " · ". Both are
          absent only when `starts_at` failed to parse, which the schema's CHECK constraint
          makes very hard to arrange. */}
      {when.join(' · ')} ·{' '}
      {venueSlug === null ? (
        venue
      ) : (
        <button type="button" className={s.venuelink} onClick={() => onVenue(venueSlug)}>
          {venue}
        </button>
      )}
    </div>
  )
}
